import type { UserLeagueSummary } from '@/api/auth';
import { isLeaguePreStart } from '@/utils/leagueLifecycle';

type LeagueLandingSummary = Pick<UserLeagueSummary, 'league_id' | 'team_id' | 'draft_status'>;

/**
 * Canonical destination for a generic league entry point such as My Leagues,
 * a summary card, or recovery from a broken league URL. Pre-start entry stays
 * neutral so LeagueLayout can route commissioners and members by role.
 */
export function getLeagueLandingPath(league: LeagueLandingSummary): string {
  if (isLeaguePreStart(league.draft_status)) {
    return `/leagues/${league.league_id}/league`;
  }
  return league.team_id
    ? `/leagues/${league.league_id}/my-team/${league.team_id}`
    : `/leagues/${league.league_id}/my-team`;
}

/** A matchup preview is a specific intent and should open the matchup shown. */
export function getLeagueMatchupPath(leagueId: number, fantasyWeek?: number | null): string {
  return fantasyWeek != null
    ? `/leagues/${leagueId}/matchup/week/${fantasyWeek}`
    : `/leagues/${leagueId}/matchup`;
}

/**
 * LeagueLayout owns lifecycle redirects for real child routes. Unknown child
 * paths must bypass that gate so the nested branded 404 can render.
 */
export function isKnownLeagueRoutePath(pathname: string, routeLeagueId: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'leagues' || segments[1] !== routeLeagueId) return false;

  const child = segments.slice(2);
  if (child.length === 0) return true;

  const [section] = child;
  if (section === 'setup' || section === 'league' || section === 'commish' || section === 'settings'
    || section === 'schedule' || section === 'players' || section === 'draft') {
    return child.length === 1;
  }
  if (section === 'matchup') {
    return child.length === 1 || (child.length === 3 && child[1] === 'week');
  }
  if (section === 'teams') {
    return child.length === 2 || (child.length === 4 && child[2] === 'week');
  }
  if (section === 'my-team') {
    return child.length === 1
      || child.length === 2
      || (child.length === 3 && child[1] === 'week')
      || (child.length === 4 && child[2] === 'week');
  }
  if (section === 'admin') {
    return child.length === 2 && [
      'roster-management',
      'set-initial-waiver-order',
      'settings',
    ].includes(child[1]);
  }
  return false;
}
